import { logger } from "./logger";

/**
 * GPU-capability verdict produced at app boot. Determines whether the
 * Cornerstone3D + VTK volume pipeline is viable, or whether we must fall
 * back to StackViewport for every series.
 *
 * "volume-ok"   = boot probe passed and renderer is not on the denylist.
 * "stack-only"  = boot probe failed, renderer denylisted, or user forced it.
 *
 * Root cause this is built for: Intel UHD-class iGPUs on Windows + ANGLE
 * D3D11 fail to compile vtkVolumeVS.glsl under load, triggering
 * CONTEXT_LOST_WEBGL and a cascade of "volume load cancelled" errors that
 * white-out the viewer. Field report, the captured CONTEXT_LOST_WEBGL chain and
 * why this is a GPU/driver-layer fix rather than the RAM/CPU one in #461: #467.
 */
export type GpuVerdict = "volume-ok" | "stack-only";

export const GPU_VERDICT_STORAGE_KEY = "a2z.gpuVerdict.v1";
export const GPU_VERDICT_OVERRIDE_KEY = "a2z.gpuVerdict.override.v1";
export const GPU_VERDICT_FORCE_PARAM = "force_stack";

// Renderer strings (lowercased substring match) known to fail VTK volume
// rendering. Conservative: only entries with reproducible failure reports.
// UNMASKED_RENDERER_WEBGL via WEBGL_debug_renderer_info.
//
// "intel(r) uhd graphics (0x..." catches drivers that don't surface a marketing
// name and fall back to "(0x<deviceId>)" — empirically the entire class behind
// this format is iGPU-tier and fails the real vtkVolumeVS.glsl compile (Joe
// Dillard report: device 0x8A56, Ice Lake UHD G1).
export const RENDERER_DENYLIST = [
  "intel(r) uhd graphics 730",
  "intel(r) uhd graphics 750",
  "intel(r) uhd graphics 630",
  "intel(r) hd graphics", // pre-UHD Intel iGPUs (all generations)
  "intel(r) uhd graphics (0x",
  "microsoft basic render driver", // software fallback
  "swiftshader", // Chrome software renderer
] as const;

/** Pure renderer-string substring match against the denylist. Null = unknown,
 *  treated as ok (let the shader probe make the final call). */
export function checkRendererString(rendererString: string | null): "ok" | "deny" {
  if (!rendererString) return "ok";
  const lc = rendererString.toLowerCase();
  for (const bad of RENDERER_DENYLIST) {
    if (lc.includes(bad)) return "deny";
  }
  return "ok";
}

/** Manual escape hatch. URL ?force_stack={0,1} > localStorage > null.
 *  Returns null when no override is in effect — caller then runs the probe. */
export function resolveOverride(loc: Location, overrideStorage: Storage): GpuVerdict | null {
  const params = new URLSearchParams(loc.search);
  const urlVal = params.get(GPU_VERDICT_FORCE_PARAM);
  if (urlVal === "1") return "stack-only";
  if (urlVal === "0") return "volume-ok";

  const stored = overrideStorage.getItem(GPU_VERDICT_OVERRIDE_KEY);
  if (stored === "stack-only" || stored === "volume-ok") return stored;
  return null;
}

// Vertex shader mimics vtkVolumeVS.glsl preamble: WebGL2 GLSL ES 3.00,
// highp float, in qualifier, mat4 uniform, position-only transform.
// Emits v_uv so the FS varying has a matching VS output (required by
// GLSL 300 es link-time checks).
const PROBE_VS_SOURCE = `#version 300 es
precision highp float;
in vec4 vertexDC;
out vec3 v_uv;
uniform mat4 PCVCMatrix;
void main() {
  gl_Position = PCVCMatrix * vertexDC;
  v_uv = vertexDC.xyz;
}
`;

// Fragment shader declares the single feature most likely to fail on weak
// iGPU/ANGLE drivers: sampler3D. Used only for compile/link check; never run.
const PROBE_FS_SOURCE = `#version 300 es
precision highp float;
precision highp sampler3D;
uniform sampler3D u_volume;
in vec3 v_uv;
out vec4 fragColor;
void main() { fragColor = texture(u_volume, v_uv); }
`;

function compileOne(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, source);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(sh);
    logger.warn("[gpuVerdict] probe shader compile failed:", info);
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

/** Compile + link the probe shaders. Returns "ok" iff both succeed.
 *  Caller is responsible for creating the WebGL2 context. */
export function probeShaders(gl: WebGL2RenderingContext): "ok" | "deny" {
  const vs = compileOne(gl, gl.VERTEX_SHADER, PROBE_VS_SOURCE);
  if (!vs) return "deny";

  const fs = compileOne(gl, gl.FRAGMENT_SHADER, PROBE_FS_SOURCE);
  if (!fs) {
    gl.deleteShader(vs);
    return "deny";
  }

  const prog = gl.createProgram();
  if (!prog) {
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    return "deny";
  }

  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);

  const ok = gl.getProgramParameter(prog, gl.LINK_STATUS);
  if (!ok) {
    logger.warn("[gpuVerdict] probe link failed:", gl.getProgramInfoLog(prog));
  }

  gl.deleteShader(vs);
  gl.deleteShader(fs);
  gl.deleteProgram(prog);
  return ok ? "ok" : "deny";
}

/** Reads UNMASKED_RENDERER_WEBGL via WEBGL_debug_renderer_info. Returns null
 *  when the extension is unavailable — privacy-restricted in some browsers. */
export function extractRenderer(gl: WebGL2RenderingContext): string | null {
  const ext = gl.getExtension("WEBGL_debug_renderer_info");
  if (!ext) return null;
  const r = gl.getParameter((ext as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL);
  return typeof r === "string" ? r : null;
}

/** Why a verdict was reached — lets `runGpuProbe` decide whether the result is
 *  deterministic enough to persist. `denylist` and `probe-pass` are stable for a
 *  given GPU; `probe-deny` / `no-context` can be a transient boot-time failure
 *  (e.g. a WebGL2 context not ready at page-init — observed on Safari) and must
 *  NOT be frozen into the cache. `override` = a manual ?force_stack / stored
 *  override decided it, no probe ran. */
export type VerdictReason = "denylist" | "probe-pass" | "probe-deny" | "no-context" | "override";

export interface DetailedVerdict {
  verdict: GpuVerdict;
  reason: VerdictReason;
}

/** Two-gate verdict + the reason: renderer-string denylist and shader probe
 *  must both pass for volume-ok; either denies → stack-only. */
export function computeVerdictDetailed(gl: WebGL2RenderingContext): DetailedVerdict {
  const renderer = extractRenderer(gl);
  if (checkRendererString(renderer) === "deny") {
    logger.info("[gpuVerdict] renderer denylisted, routing to stack-only:", renderer);
    return { verdict: "stack-only", reason: "denylist" };
  }
  if (probeShaders(gl) === "deny") {
    logger.info("[gpuVerdict] probe failed, routing to stack-only. renderer:", renderer);
    return { verdict: "stack-only", reason: "probe-deny" };
  }
  logger.info("[gpuVerdict] probe passed, renderer:", renderer);
  return { verdict: "volume-ok", reason: "probe-pass" };
}

/** Verdict only — back-compat wrapper around {@link computeVerdictDetailed}. */
export function computeVerdict(gl: WebGL2RenderingContext): GpuVerdict {
  return computeVerdictDetailed(gl).verdict;
}

export interface RunGpuProbeDeps {
  location: Location;
  /** Persistent storage for the ops escape hatch (production: localStorage). */
  overrideStorage: Storage;
  /** Per-tab storage for the probe-result cache (production: sessionStorage). */
  cacheStorage: Storage;
  /** Test-injectable factory. Production passes the offscreen-canvas-backed
   *  WebGL2 context; tests pass a mock. Return null to simulate no-WebGL2. */
  createContext: () => WebGL2RenderingContext | null;
}

/** How many times to (re-)create the context + probe before settling on
 *  stack-only. A capable GPU that transiently fails at page-init (observed on
 *  Safari: the WebGL2 context isn't ready on the first attempt) passes on a
 *  retry, so a single flaky failure can't decide the verdict. */
export const PROBE_ATTEMPTS = 3;

/** Resolves the verdict in priority order:
 *    1. URL override (?force_stack=0/1)
 *    2. localStorage override (a2z.gpuVerdict.override.v1)
 *    3. sessionStorage cached verdict from this tab (a2z.gpuVerdict.v1)
 *    4. Probe with bounded retries
 *
 *  DETERMINISM RULE (fixes the cross-browser/tab inconsistency): only a
 *  `volume-ok` or a `denylist` stack-only is cached — those are stable for a
 *  given GPU. A `stack-only` from a transient probe-deny / missing context is
 *  used for THIS load but NEVER frozen, so a capable machine that flaked once
 *  re-probes (and recovers) next load instead of being stuck on the weak-GPU
 *  path forever. The verdict stays observable + resettable via the banner. */
export function runGpuProbeDetailed(deps: RunGpuProbeDeps): DetailedVerdict {
  const { location, overrideStorage, cacheStorage, createContext } = deps;

  const override = resolveOverride(location, overrideStorage);
  if (override !== null) {
    logger.info("[gpuVerdict] override active:", override);
    return { verdict: override, reason: "override" };
  }

  // The reason is recoverable from the cached verdict alone because of the
  // DETERMINISM RULE above: a cached stack-only can only have come from the
  // denylist. If that rule ever caches another stack-only, cache the reason too.
  const cached = cacheStorage.getItem(GPU_VERDICT_STORAGE_KEY);
  if (cached === "stack-only") return { verdict: "stack-only", reason: "denylist" };
  if (cached === "volume-ok") return { verdict: "volume-ok", reason: "probe-pass" };

  // Probe with bounded retries. Any attempt that yields volume-ok wins; a
  // denylist hit is authoritative immediately. Only when every attempt fails
  // transiently do we settle on a (non-frozen) stack-only.
  let result: DetailedVerdict = { verdict: "stack-only", reason: "no-context" };
  for (let attempt = 0; attempt < PROBE_ATTEMPTS; attempt++) {
    let gl: WebGL2RenderingContext | null = null;
    try {
      gl = createContext();
    } catch (e) {
      logger.warn("[gpuVerdict] createContext threw:", e);
      gl = null;
    }
    if (!gl) {
      result = { verdict: "stack-only", reason: "no-context" };
      continue;
    }
    try {
      result = computeVerdictDetailed(gl);
    } catch (e) {
      logger.warn("[gpuVerdict] probe threw:", e);
      result = { verdict: "stack-only", reason: "no-context" };
      continue;
    }
    if (result.verdict === "volume-ok" || result.reason === "denylist") break;
  }

  // Persist ONLY deterministic verdicts; never freeze a transient stack-only.
  const deterministic = result.verdict === "volume-ok" || result.reason === "denylist";
  try {
    if (deterministic) {
      cacheStorage.setItem(GPU_VERDICT_STORAGE_KEY, result.verdict);
    } else {
      // Drop any stale cached value so this load's transient stack-only can't
      // linger — the next load re-probes and a capable machine recovers.
      cacheStorage.removeItem(GPU_VERDICT_STORAGE_KEY);
      logger.info(
        "[gpuVerdict] transient stack-only (reason=%s) — not cached, will re-probe next load",
        result.reason,
      );
    }
  } catch (e) {
    // sessionStorage can throw (quota, private mode). Verdict is still valid
    // for this load; persistence just won't survive.
    logger.warn("[gpuVerdict] failed to persist verdict:", e);
  }

  return result;
}

/** Verdict only — back-compat wrapper around {@link runGpuProbeDetailed}. */
export function runGpuProbe(deps: RunGpuProbeDeps): GpuVerdict {
  return runGpuProbeDetailed(deps).verdict;
}

/** Default production context factory. Creates a tiny offscreen canvas and
 *  requests a WebGL2 context. preserveDrawingBuffer:false to minimize VRAM. */
export function defaultCreateContext(): WebGL2RenderingContext | null {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  return canvas.getContext("webgl2", {
    preserveDrawingBuffer: false,
  }) as WebGL2RenderingContext | null;
}

/** Clear the cached verdict AND any manual override so the next load probes
 *  fresh. Backs the banner's "Reload to try 3D" recovery action — the explicit
 *  escape from a wrong/stale verdict that left a capable machine on the
 *  weak-GPU path. Defaults to the production storages. */
export function resetGpuVerdict(
  cacheStorage: Storage = sessionStorage,
  overrideStorage: Storage = localStorage,
): void {
  try {
    cacheStorage.removeItem(GPU_VERDICT_STORAGE_KEY);
  } catch (e) {
    logger.warn("[gpuVerdict] failed to clear cached verdict:", e);
  }
  try {
    overrideStorage.removeItem(GPU_VERDICT_OVERRIDE_KEY);
  } catch (e) {
    logger.warn("[gpuVerdict] failed to clear override:", e);
  }
}
